import { ActionContext, ActionTree } from 'vuex'
import {
  ICouponsState,
  ICouponInformation,
  IPlan,
  IProduct,
  IProductExtended,
  IRootState,
} from '@/@types'
import { fetchCouponInfo } from '@/models/coupons'
import { isLiveVirtualTrainingCoupon } from '@/helpers/coupons'
import CONSTANTS from '@/constants'

const shouldBlockCyberMondayBusinessCoupon = (
  context: ActionContext<ICouponsState, IRootState>,
  couponStr: string
) => {
  if (!couponStr) {
    return false
  }
  const isBusinessUser = context.rootGetters['business/isBusinessUser']
  const isCyberMondayActive = context.rootGetters['featureFlags/isCyberMondayActive']
  const couponCode = couponStr.toLowerCase()
  const isRestrictedCoupon = CONSTANTS.CYBER_MONDAY_BUSINESS_RESTRICTED_COUPONS.includes(couponCode)
  return isBusinessUser && isCyberMondayActive && isRestrictedCoupon
}

const addCouponToStack = (
  context: ActionContext<ICouponsState, IRootState>,
  couponStr: string,
  couponObj: ICouponInformation
) => {
  if (
    !couponStr ||
    (context.state.couponStrStack && context.state.couponStrStack.includes(couponStr))
  ) {
    return
  }
  context.commit('COUPON_STR_STACK_UPDATED', [...(context.state.couponStrStack || []), couponStr])
  context.commit('COUPON_OBJ_STACK_UPDATED', [...(context.state.couponObjStack || []), couponObj])
}

const isCertOnCart = (cart: IProductExtended[]) => {
  return cart.some(item => Object.values(CONSTANTS.VOUCHERS).includes(item.merchant_id))
}

const actions: ActionTree<ICouponsState, IRootState> = {
  addCoupon: async (context: ActionContext<ICouponsState, IRootState>, couponStr: string) => {
    try {
      const sanitizedCouponStr = (couponStr || '').trim()
      if (shouldBlockCyberMondayBusinessCoupon(context, sanitizedCouponStr)) {
        const restrictedCouponObj: ICouponInformation = {
          code: sanitizedCouponStr,
          couponStr: sanitizedCouponStr,
          isEligibleForCart: false,
        }
        context.commit('COUPON_UPDATED', sanitizedCouponStr)
        context.commit('COUPON_OBJ_UPDATED', restrictedCouponObj)
        addCouponToStack(context, sanitizedCouponStr, restrictedCouponObj)
        context.dispatch('calculateCouponTotalDiscount')
        return
      }

      const couponObj = await context.dispatch('getCouponObj', sanitizedCouponStr)
      const shoppingCart = context.rootGetters['shoppingCart/getShoppingCart'] || []
      const isPromotionActive = context.rootGetters['app/isPromotionActive']
      const targetsCerts =
        (couponObj.items || []).some((item: { code: string }) =>
          Object.values(CONSTANTS.VOUCHERS).includes(item.code)
        ) ||
        (couponObj.plans || []).some((plan: { code: string }) =>
          Object.values(CONSTANTS.VOUCHERS).includes(plan.code)
        )
      const certsInCart = isCertOnCart(shoppingCart)
      const isNotHundredPercent =
        couponObj?.discount?.type !== 'percent' || couponObj?.discount?.percent !== 100
      const shouldBlockCertCoupon =
        targetsCerts && certsInCart && isPromotionActive && isNotHundredPercent

      context.commit('COUPON_UPDATED', sanitizedCouponStr)

      if (shouldBlockCertCoupon) {
        couponObj.isEligibleForCart = false
        couponObj.discountAmount = 0
        couponObj.discountAmountCents = 0
        context.commit('COUPON_OBJ_UPDATED', couponObj)
        addCouponToStack(context, sanitizedCouponStr, couponObj)
        context.dispatch('calculateCouponTotalDiscount')
        return
      }

      await context.dispatch('updateCouponEligibility', couponObj)
      await context.dispatch('calculateCouponDiscount', couponObj)

      const couponForRecurly = couponObj?.isEligibleForCart ? sanitizedCouponStr : ''
      await context.dispatch('recurly/setRecurlyCoupon', couponForRecurly, { root: true })

      addCouponToStack(context, sanitizedCouponStr, couponObj)
      context.dispatch('calculateCouponTotalDiscount')
    } catch (error) {
      context.commit('COUPON_UPDATED', '')
      throw error
    }
  },
  removeCoupon: (context: ActionContext<ICouponsState, IRootState>, couponStr: string) => {
    try {
      context.commit('COUPON_UPDATED', null)
      context.commit('COUPON_OBJ_UPDATED', null)
      context.dispatch('recurly/setRecurlyCoupon', '', { root: true })
      if (
        couponStr &&
        (context.state.couponStrStack || context.state.couponStrStack!.includes(couponStr))
      ) {
        const updatedCouponStrStack = (context.state.couponStrStack || []).filter(
          item => item !== couponStr
        )
        context.commit('COUPON_STR_STACK_UPDATED', updatedCouponStrStack)

        const updatedCouponObjStack = (context.state.couponObjStack || []).filter(
          item => item.couponStr !== couponStr
        )
        context.commit('COUPON_OBJ_STACK_UPDATED', updatedCouponObjStack)
        context.dispatch('calculateCouponTotalDiscount')
      }
    } catch (error) {
      context.commit('COUPON_UPDATED', null)
      context.commit('COUPON_OBJ_UPDATED', null)
      throw error
    }
  },
  getCouponObj: async (context: ActionContext<ICouponsState, IRootState>, couponStr: string) => {
    try {
      context.commit('COUPON_LOADING_UPDATED', true)
      const couponObj = await fetchCouponInfo(couponStr)
      couponObj.couponStr = couponStr
      context.commit('COUPON_OBJ_UPDATED', couponObj)
      context.commit('COUPON_LOADING_UPDATED', false)
      return couponObj
    } catch (error: any) {
      const couponError = {
        ...error,
        code: couponStr,
        couponStr,
        codeError: error.code,
      }
      context.commit('COUPON_OBJ_UPDATED', couponError)
      context.commit('COUPON_LOADING_UPDATED', false)
      return couponError
    }
  },
  updateCouponEligibility(
    context: ActionContext<ICouponsState, IRootState>,
    couponObj: ICouponInformation
  ) {
    let isEligible = false
    if (couponObj.discount) {
      const is100PercentDiscount = context.rootGetters['coupons/is100PercentOffCoupon']
      const cartElements = context.rootGetters['shoppingCart/getShoppingCart']
      const isCertPromotionActive =
        context.rootGetters['featureFlags/isSummerSavingsEnabled'] ||
        context.rootGetters['featureFlags/isFlashSaleEnabled'] ||
        context.rootGetters['featureFlags/isCerts50percentDiscountEnabled']
      const isCyberMondayActive = context.rootGetters['app/isCyberMondayActive']
      const isLiveVirtualCoupon = isLiveVirtualTrainingCoupon(couponObj)

      const cartIds = cartElements.map((item: IProductExtended) => item.merchant_id)
      const cartHasItemWithDiscount = cartElements.some(
        (item: IProductExtended) => item.user_receives_discount
      )

      if (couponObj.items && couponObj.items.length > 0) {
        isEligible = couponObj.items.some(item => cartIds.includes(item.code))
      }
      if (couponObj.plans && couponObj.plans.length > 0) {
        isEligible = couponObj.plans.some(plan => cartIds.includes(plan.code))
      }

      if (isEligible && isCertPromotionActive) {
        const shouldIgnoreDiscountRestrictions = isCyberMondayActive && isLiveVirtualCoupon
        if (!shouldIgnoreDiscountRestrictions) {
          isEligible = is100PercentDiscount || !cartHasItemWithDiscount
        }
      }
    }

    context.commit('COUPON_ELIGIBILITY_UPDATED', { couponObj, isEligible })
  },
  async updateCouponObjStack(context: ActionContext<ICouponsState, IRootState>) {
    context.commit('COUPON_LOADING_UPDATED', true)
    const coupons = context.state.couponObjStack || []
    await coupons.reduce(async (prevPromise: Promise<void>, couponObj) => {
      await prevPromise
      await context.dispatch('updateCouponEligibility', couponObj)
      await context.dispatch('calculateCouponDiscount', couponObj)
    }, Promise.resolve())
    await context.dispatch('calculateCouponTotalDiscount')
    context.commit('COUPON_LOADING_UPDATED', false)
  },
  calculateCouponDiscount: (
    context: ActionContext<ICouponsState, IRootState>,
    couponObj: ICouponInformation
  ) => {
    try {
      const isPromotionActive = context.rootGetters['app/isPromotionActive']
      const cartHasAnySubscription = context.rootGetters['shoppingCart/cartHasAnySubscription']
      const { discount, isEligibleForCart } = couponObj

      if (!isEligibleForCart) {
        context.commit('COUPON_DISCOUNT_UPDATED', { couponObj, discountAmount: 0 })
        return
      }

      const cart = context.rootGetters['shoppingCart/getShoppingCart']
      let totalDiscount = 0

      const couponProductCodes = [...(couponObj.items || []), ...(couponObj.plans || [])].map(
        item => item.code
      )
      const getDiscountPercent = (itemOrAddOn: IPlan | IProductExtended | any, percent: number) => {
        const { user_receives_discount, discounted_price_cents, price_cents } = itemOrAddOn

        let currentItemPrice

        if (isPromotionActive) {
          currentItemPrice =
            user_receives_discount || (!user_receives_discount && cartHasAnySubscription)
              ? discounted_price_cents
              : price_cents
        } else {
          currentItemPrice = user_receives_discount ? discounted_price_cents : price_cents
        }

        return (percent * (currentItemPrice! / 100)) / 100
      }

      if (discount && ['percent', 'fixed'].includes(discount.type)) {
        switch (discount.type) {
          case 'percent': {
            const { percent } = discount
            cart.forEach((item: IPlan | IProduct | any) => {
              if (couponProductCodes.includes(item.merchant_id)) {
                totalDiscount += getDiscountPercent(item, percent!)
                if (item.addOnsOnCart) {
                  item.addOnsOnCart.forEach((addOn: IPlan | IProduct | any) => {
                    totalDiscount += getDiscountPercent(addOn, percent!)
                  })
                }
                totalDiscount = parseFloat(totalDiscount.toFixed(2))
              }
            })
            break
          }
          case 'fixed': {
            const { amount } = discount
            totalDiscount += amount!
            break
          }
        }
      }
      context.commit('COUPON_DISCOUNT_UPDATED', { couponObj, discountAmount: totalDiscount })
    } catch (error) {
      context.commit('COUPON_DISCOUNT_UPDATED', { couponObj, discountAmount: 0 })
    }
  },
  calculateCouponTotalDiscount: (context: ActionContext<ICouponsState, IRootState>) => {
    try {
      let couponTotalDiscount = 0
      let couponTotalDiscountCents = 0
      context.state.couponObjStack?.forEach(couponObj => {
        const { discountAmount, discountAmountCents } = couponObj
        couponTotalDiscount += discountAmount || 0
        couponTotalDiscountCents += discountAmountCents || 0
      })
      context.commit('COUPON_DISCOUNT_TOTAL_UPDATED', {
        couponTotalDiscount,
        couponTotalDiscountCents,
      })
      context.dispatch('priceDetails/priceDetailsUpdate', {}, { root: true })
    } catch (error) {
      context.commit('COUPON_DISCOUNT_TOTAL_UPDATED', {
        couponTotalDiscount: 0,
        couponTotalDiscountCents: 0,
      })
      context.dispatch('priceDetails/priceDetailsUpdate', {}, { root: true })
    }
  },
}

export default actions
